// toast.ts — UI-agnostic toast bus. Hooks/services emit toasts here without
// importing any component; the Toast UI (Phase 4) subscribes via subscribeToasts
// and renders them. This permanently decouples the data layer from presentation.
export type ToastTone = "success" | "info" | "warn" | "danger";

export interface ToastInput {
  kind: ToastTone;
  title: string;
  body?: string;
  /** Re-render in place instead of stacking when two toasts share a key. */
  dedupKey?: string;
  /** Override the default visible duration (ms). */
  ttlMs?: number;
}

export interface ToastRecord extends ToastInput {
  id: number;
}

type Listener = (t: ToastRecord) => void;

const listeners = new Set<Listener>();
let seq = 0;

/** Emit a toast to every subscriber. Safe to call with no UI mounted (no-op). */
export function emitToast(t: ToastInput): void {
  const rec: ToastRecord = { ...t, id: ++seq };
  for (const l of listeners) l(rec);
}

/** Subscribe the Toast UI to the bus. Returns an unsubscribe fn. */
export function subscribeToasts(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Back-compat hook used by useOrderStatusStream:
 *  `const toast = useToast(); toast.push({...})`. */
export function useToast(): { push: (t: ToastInput) => void } {
  return { push: emitToast };
}
